import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Lock, Eye, EyeOff, CheckCircle2, AlertCircle, ShieldCheck,
  User, Mail, Phone, MapPin, Linkedin, Globe, Languages, Briefcase, Mic,
  KeyRound, Loader2, Plus, Check, ChevronDown,
} from 'lucide-react';
import { api, formatApiError, absoluteUrl } from '../lib/api';
import logo from '../assets/logo.png';
import ImageUploader from '../components/ImageUploader';
import InterpreterProfilePreview from '../components/InterpreterProfilePreview';
import FullProfilePreview from '../components/FullProfilePreview';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import SupportContactButton from '../components/SupportContactButton';
import { useClickLog } from '../hooks/useClickLog';

// ── Chip multi-select with catalog suggestions ───────────────────────────────
export function TagSelect({ label, icon: Icon, value, onChange, options, placeholder, testid, hint }) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = React.useRef(null);
  const add = (v) => {
    const t = (v || '').trim();
    if (!t) return;
    if (!value.some((x) => x.toLowerCase() === t.toLowerCase())) onChange([...value, t]);
    setInput('');
    // Keep the dropdown open and focused so the user can add several in a row
    // without having to click out and back in.
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const remove = (t) => onChange(value.filter((x) => x !== t));
  const suggestions = (options || [])
    .filter((o) => !value.some((x) => x.toLowerCase() === o.toLowerCase()) && o.toLowerCase().includes(input.toLowerCase()))
    .slice(0, 60);
  const showCreate = input.trim() && !suggestions.some((s) => s.toLowerCase() === input.trim().toLowerCase())
    && !value.some((x) => x.toLowerCase() === input.trim().toLowerCase());
  return (
    <div>
      <label className="block text-xs font-semibold text-charcoal-700 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={13} className="text-cmic-500" />} {label}
      </label>
      <div className="relative">
        <div
          className="rounded-xl border border-gray-200 focus-within:ring-2 focus-within:ring-cmic-500/30 focus-within:border-cmic-500 px-2.5 py-2 cursor-text"
          onClick={() => { setOpen(true); inputRef.current?.focus(); }}
        >
          <div className="flex flex-wrap gap-1.5 items-center">
            {value.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-sm font-medium bg-cmic-50 text-cmic-700 border border-cmic-100 px-2.5 py-1 rounded-lg">
                {t}
                <button type="button" onClick={(e) => { e.stopPropagation(); remove(t); }} className="text-cmic-400 hover:text-rose-600 text-base leading-none -mr-0.5" aria-label={`Quitar ${t}`}>×</button>
              </span>
            ))}
            <div className="flex-1 min-w-[8rem] flex items-center">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => { setInput(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); add(input || suggestions[0]); }
                  if (e.key === 'Backspace' && !input && value.length) remove(value[value.length - 1]);
                }}
                placeholder={value.length ? 'Agregar otro…' : placeholder}
                data-testid={testid}
                className="w-full text-sm py-1 px-1 focus:outline-none bg-transparent"
              />
              <ChevronDown size={16} className={`text-charcoal-300 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </div>

        {open && (suggestions.length > 0 || showCreate) && (
          <div
            data-testid={testid ? `${testid}-dropdown` : undefined}
            className="absolute z-30 left-0 right-0 top-full mt-2 max-h-72 overflow-auto bg-white border border-gray-200 rounded-2xl shadow-xl py-1.5"
          >
            {showCreate && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(input)}
                data-testid={testid ? `${testid}-create` : undefined}
                className="w-full text-left px-4 py-3 text-sm text-cmic-700 font-medium hover:bg-cmic-50 flex items-center gap-2.5 border-b border-gray-100"
              >
                <span className="w-7 h-7 rounded-lg bg-cmic-100 flex items-center justify-center flex-shrink-0"><Plus size={15} className="text-cmic-600" /></span>
                Agregar “{input.trim()}”
              </button>
            )}
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(s)}
                data-testid={testid ? `${testid}-option-${s.replace(/\W+/g, '-').toLowerCase()}` : undefined}
                className="group w-full text-left px-4 py-2.5 text-sm text-charcoal-700 hover:bg-cmic-50 flex items-center justify-between gap-2"
              >
                <span>{s}</span>
                <Check size={15} className="text-cmic-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
      {hint && <p className="text-[11px] text-charcoal-400 mt-1.5">{hint}</p>}
    </div>
  );
}

function Input({ label, icon: Icon, value, onChange, type = 'text', placeholder, required, readOnly, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-charcoal-700 mb-1.5">{label}{required && <span className="text-rose-500"> *</span>}</label>
      <div className="relative">
        {Icon && <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          data-testid={testid}
          className={`w-full rounded-xl border border-gray-200 ${Icon ? 'pl-9' : 'pl-4'} pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 ${readOnly ? 'bg-gray-50 text-charcoal-500' : ''}`}
        />
      </div>
    </div>
  );
}

function SectionTitle({ n, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-cmic-600 text-white text-xs font-bold flex items-center justify-center">{n}</span>
      <div>
        <h3 className="font-semibold text-charcoal-800 leading-tight">{title}</h3>
        {subtitle && <p className="text-xs text-charcoal-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

// ── Live profile preview now uses the shared InterpreterProfilePreview ───────

const EMPTY = {
  nombres: '', apellido_paterno: '', apellido_materno: '',
  password: '', confirmPwd: '', email: '',
  phone: '', city: '', linkedin_url: '', website: '', bio: '', photo_url: '',
  additional_emails: [], additional_phones: [],
  active_languages: [], passive_languages: [], specialty: [], modalities: [],
};

export default function RegisterConfirmPage() {
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [tokenChecking, setTokenChecking] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [sq, setSq] = useState([{ question_id: 0, answer: '' }, { question_id: 0, answer: '' }]);
  const [sqCatalog, setSqCatalog] = useState([]);
  const [catalogs, setCatalogs] = useState({ languages: [], specialties: [], modalities: [] });
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [restoredAt, setRestoredAt] = useState(null);
  const { logRef, log, onClickCapture } = useClickLog();
  const DRAFT_KEY = token ? `cmic_register_draft_v1_${token}` : null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { log('page_load', { page: 'register-confirm' }); }, [log]);
  useEffect(() => { if (submitError) log('error', { message: submitError }); }, [submitError, log]);

  // ── Autosave: restore any locally-saved draft once the token is validated ──
  useEffect(() => {
    if (!DRAFT_KEY || !tokenInfo || done) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft && draft.form) {
        setForm((f) => ({ ...f, ...draft.form }));
        if (Array.isArray(draft.sq)) {
          setSq((prev) => prev.map((x, i) => (draft.sq[i] ? { ...x, question_id: draft.sq[i].question_id || x.question_id } : x)));
        }
        setRestoredAt(draft.savedAt || Date.now());
        log('draft_restored', { savedAt: draft.savedAt });
      }
    } catch { /* ignore corrupt draft */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenInfo]);

  // Persist a draft as the user types (excludes password fields for safety).
  useEffect(() => {
    if (!DRAFT_KEY || !tokenInfo || done) return;
    const id = setTimeout(() => {
      try {
        const { password, confirmPwd, ...rest } = form;
        const draft = { form: rest, sq: sq.map((s) => ({ question_id: s.question_id })), savedAt: Date.now() };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      } catch { /* storage full / unavailable */ }
    }, 600);
    return () => clearTimeout(id);
  }, [form, sq, DRAFT_KEY, tokenInfo, done]);

  function discardDraft() {
    if (DRAFT_KEY) { try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ } }
    setRestoredAt(null);
    setForm((f) => {
      const base = { ...EMPTY, email: f.email };
      if (tokenInfo?.full_name) {
        const parts = tokenInfo.full_name.split(' ');
        base.nombres = parts[0] || '';
        base.apellido_paterno = parts[1] || '';
        base.apellido_materno = parts.slice(2).join(' ') || '';
      }
      return base;
    });
    setSq([{ question_id: 0, answer: '' }, { question_id: 0, answer: '' }]);
  }

  useEffect(() => {
    if (!token) { setTokenError('Falta el token de confirmación en el enlace.'); setTokenChecking(false); return; }
    let alive = true;
    api.get('/auth/register-token-info', { params: { token } })
      .then(({ data }) => {
        if (!alive) return;
        setTokenInfo(data);
        if (data.full_name) {
          const parts = data.full_name.split(' ');
          setForm((f) => ({ ...f, nombres: parts[0] || '', apellido_paterno: parts[1] || '', apellido_materno: parts.slice(2).join(' ') || '' }));
        }
      })
      .catch((err) => { if (alive) setTokenError(formatApiError(err.response?.data?.detail) || 'Enlace inválido'); })
      .finally(() => { if (alive) setTokenChecking(false); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    api.get('/auth/security-questions-catalog').then(({ data }) => setSqCatalog(data || [])).catch(() => {});
    api.get('/auth/registration-catalogs').then(({ data }) => setCatalogs(data || { languages: [], specialties: [], modalities: [] })).catch(() => {});
  }, []);

  const fullName = useMemo(() => [form.nombres, form.apellido_paterno, form.apellido_materno].filter(Boolean).join(' ').trim(), [form]);
  const isGeneric = !!tokenInfo?.generic;

  function validate() {
    if (isGeneric) {
      const em = form.email.trim();
      if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return 'Ingresa un correo electrónico válido.';
    }
    if (!form.nombres.trim() || !form.apellido_paterno.trim()) return 'Nombre y apellido paterno son obligatorios.';
    if (form.password.length < 8) return 'La contraseña debe tener al menos 8 caracteres.';
    if (form.password !== form.confirmPwd) return 'Las contraseñas no coinciden.';
    if (!sq[0].question_id || !sq[1].question_id) return 'Elige tus 2 preguntas de seguridad.';
    if (sq[0].question_id === sq[1].question_id) return 'Las 2 preguntas de seguridad deben ser distintas.';
    if (sq[0].answer.trim().length < 2 || sq[1].answer.trim().length < 2) return 'Responde ambas preguntas de seguridad (mín. 2 caracteres).';
    if (form.active_languages.length === 0) return 'Agrega al menos un idioma activo.';
    return '';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setSubmitError(err); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSubmitError('');
    setSubmitting(true);
    try {
      await api.post('/auth/register-confirm', {
        token,
        password: form.password,
        ...(isGeneric ? { email: form.email.trim() } : {}),
        nombres: form.nombres.trim(),
        apellido_paterno: form.apellido_paterno.trim(),
        apellido_materno: form.apellido_materno.trim(),
        full_name: fullName,
        phone: form.phone.trim(),
        city: form.city.trim(),
        additional_emails: form.additional_emails,
        additional_phones: form.additional_phones,
        linkedin_url: form.linkedin_url.trim(),
        website: form.website.trim(),
        bio: form.bio.trim(),
        photo_url: form.photo_url,
        active_languages: form.active_languages,
        passive_languages: form.passive_languages,
        specialty: form.specialty,
        modalities: form.modalities,
        security_questions: sq.map((s) => ({ question_id: s.question_id, answer: s.answer.trim() })),
      });
      setDone(true);
      if (DRAFT_KEY) { try { localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ } }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err2) {
      setSubmitError(formatApiError(err2.response?.data?.detail) || 'No se pudo completar el registro');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render states ──────────────────────────────────────────────────────────
  if (tokenChecking) {
    return (
      <Shell>
        <div className="text-center py-16" data-testid="register-confirm-checking">
          <div className="w-10 h-10 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-charcoal-500">Validando enlace…</p>
        </div>
      </Shell>
    );
  }
  if (tokenError) {
    return (
      <Shell>
        <div className="text-center py-10 max-w-md mx-auto" data-testid="register-confirm-token-error">
          <div className="w-14 h-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4"><AlertCircle size={28} /></div>
          <h2 className="font-semibold text-charcoal-800 mb-2">Enlace inválido o expirado</h2>
          <p className="text-sm text-charcoal-600 mb-6">{tokenError}</p>
          <Link to="/login" data-testid="register-confirm-restart-link" className="inline-flex items-center gap-2 text-sm font-medium text-cmic-600 hover:text-cmic-700">Volver al inicio de sesión</Link>
        </div>
      </Shell>
    );
  }
  if (done) {
    return (
      <Shell>
        <div className="text-center py-12 max-w-md mx-auto" data-testid="register-confirm-success">
          <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4"><ShieldCheck size={32} /></div>
          <h2 className="text-xl font-bold text-charcoal-800 mb-2">¡Tu solicitud fue enviada!</h2>
          <p className="text-sm text-charcoal-600 mb-2">Tu cuenta y perfil quedan <strong>pendientes de aprobación</strong> por el consejo directivo.</p>
          <p className="text-xs text-charcoal-400 mb-6">Te enviaremos un correo cuando sea aprobada. Después podrás iniciar sesión y tu perfil aparecerá en el directorio público.</p>
          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-medium text-cmic-600 hover:text-cmic-700"><ArrowLeft size={14} />Volver al inicio de sesión</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell wide>
      <div className="grid lg:grid-cols-[1fr_22rem] gap-8 items-start" onClickCapture={onClickCapture}>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-confirm-form">
          {restoredAt && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 flex items-start justify-between gap-3" data-testid="register-draft-restored">
              <div className="flex gap-2">
                <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5 text-amber-600" />
                <span>Recuperamos tu progreso guardado automáticamente. Por seguridad, vuelve a escribir tu contraseña y respuestas de seguridad.</span>
              </div>
              <button type="button" onClick={discardDraft} data-testid="register-draft-discard" className="flex-shrink-0 font-semibold text-amber-700 hover:text-amber-900 underline">
                Empezar de nuevo
              </button>
            </div>
          )}
          <div className="bg-cmic-50 border border-cmic-100 rounded-xl px-4 py-3 text-xs text-cmic-800 flex gap-2">
            <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">{isGeneric ? 'Registro de intérprete' : `Correo verificado: ${tokenInfo?.email}`}</p>
              <p className="mt-0.5 text-cmic-700">Completa tu perfil tal como quieres que aparezca en el directorio del CMIC.{isGeneric ? ' Ingresa el correo donde quieres recibir los mensajes de contacto.' : ''}</p>
              <ul className="mt-2 space-y-1 text-cmic-700 list-disc list-inside">
                <li>Los campos marcados con <span className="text-rose-500 font-semibold">*</span> son obligatorios.</li>
                <li>En <strong>Idiomas</strong>, <strong>Modalidades</strong> y <strong>Especialidades</strong>, si no encuentras tu opción en la lista, <strong>escríbela y haz clic en “Agregar”</strong>.</li>
                <li>Revisa la <strong>vista previa</strong> a la derecha: así se verá tu perfil público completo.</li>
              </ul>
            </div>
          </div>

          {submitError && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="register-confirm-error">{submitError}</div>}

          {/* 1. Datos personales */}
          <Card>
            <SectionTitle n="1" title="Datos personales" subtitle="Tu nombre como aparecerá públicamente." />
            <div className="grid sm:grid-cols-3 gap-4">
              <Input label="Nombre(s)" icon={User} required value={form.nombres} onChange={(e) => set('nombres', e.target.value)} testid="reg-nombres" />
              <Input label="Apellido paterno" required value={form.apellido_paterno} onChange={(e) => set('apellido_paterno', e.target.value)} testid="reg-apellido-paterno" />
              <Input label="Apellido materno" value={form.apellido_materno} onChange={(e) => set('apellido_materno', e.target.value)} testid="reg-apellido-materno" />
            </div>
          </Card>

          {/* 2. Foto */}
          <Card>
            <SectionTitle n="2" title="Foto de perfil" subtitle="Opcional. Te dejaremos recortarla antes de guardar." />
            <ImageUploader
              value={form.photo_url ? absoluteUrl(form.photo_url) : ''}
              onChange={(url) => set('photo_url', url)}
              testid="reg-photo"
              shape="circle"
              uploadEndpoint="/auth/register-upload-photo"
              uploadExtra={{ token }}
            />
          </Card>

          {/* 3. Acceso */}
          <Card>
            <SectionTitle n="3" title="Contraseña" subtitle="La usarás para iniciar sesión." />
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-charcoal-700 mb-1.5">Contraseña (mín. 8) <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
                  <input type={show ? 'text' : 'password'} value={form.password} onChange={(e) => set('password', e.target.value)} required minLength={8} data-testid="reg-password" placeholder="••••••••" className="w-full rounded-xl border border-gray-200 pl-9 pr-12 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal-400 hover:text-charcoal-600">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-charcoal-700 mb-1.5">Confirma tu contraseña <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
                  <input type={show ? 'text' : 'password'} value={form.confirmPwd} onChange={(e) => set('confirmPwd', e.target.value)} required minLength={8} data-testid="reg-password-confirm" placeholder="••••••••" className="w-full rounded-xl border border-gray-200 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
                </div>
              </div>
            </div>
          </Card>

          {/* 4. Preguntas de seguridad */}
          <Card>
            <SectionTitle n="4" title="Preguntas de seguridad" subtitle="Obligatorias. Te permiten recuperar tu contraseña sin correo." />
            <div className="space-y-4">
              {[0, 1].map((i) => (
                <div key={i} className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-charcoal-700 mb-1.5 flex items-center gap-1.5"><KeyRound size={12} className="text-cmic-500" />Pregunta {i + 1} <span className="text-rose-500">*</span></label>
                    <Select
                      value={sq[i].question_id ? String(sq[i].question_id) : undefined}
                      onValueChange={(v) => setSq((p) => p.map((x, idx) => idx === i ? { ...x, question_id: Number(v) } : x))}
                    >
                      <SelectTrigger
                        data-testid={`reg-sq-question-${i}`}
                        className="w-full h-auto rounded-xl border border-gray-200 px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                      >
                        <SelectValue placeholder="Elige una pregunta…" />
                      </SelectTrigger>
                      <SelectContent>
                        {sqCatalog.filter((q) => q.id === sq[i].question_id || q.id !== sq[1 - i].question_id).map((q) => (
                          <SelectItem key={q.id} value={String(q.id)} data-testid={`reg-sq-question-${i}-option-${q.id}`}>{q.es}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input label="Respuesta" required value={sq[i].answer} onChange={(e) => setSq((p) => p.map((x, idx) => idx === i ? { ...x, answer: e.target.value } : x))} testid={`reg-sq-answer-${i}`} placeholder="Tu respuesta" />
                </div>
              ))}
            </div>
          </Card>

          {/* 5. Contacto */}
          <Card>
            <SectionTitle n="5" title="Datos de contacto" />
            <div className="grid sm:grid-cols-2 gap-4">
              {isGeneric ? (
                <Input label="Correo principal" icon={Mail} required value={form.email} onChange={(e) => set('email', e.target.value)} testid="reg-email" placeholder="tu@correo.com" type="email" />
              ) : (
                <Input label="Correo principal (verificado)" icon={Mail} value={tokenInfo?.email || ''} onChange={() => {}} readOnly testid="reg-email" />
              )}
              <Input label="Teléfono principal" icon={Phone} value={form.phone} onChange={(e) => set('phone', e.target.value)} testid="reg-phone" placeholder="+52 …" />
              <Input label="Ciudad / país" icon={MapPin} value={form.city} onChange={(e) => set('city', e.target.value)} testid="reg-city" placeholder="Ciudad de México, México" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <TagSelect label="Correos adicionales" icon={Mail} value={form.additional_emails} onChange={(v) => set('additional_emails', v)} options={[]} placeholder="otro@correo.com (Enter)" testid="reg-additional-emails" hint="El principal recibe los mensajes de contacto." />
              <TagSelect label="Teléfonos adicionales" icon={Phone} value={form.additional_phones} onChange={(v) => set('additional_phones', v)} options={[]} placeholder="+52 … (Enter)" testid="reg-additional-phones" />
            </div>
          </Card>

          {/* 6. Enlaces */}
          <Card>
            <SectionTitle n="6" title="Enlaces profesionales" subtitle="Opcional." />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="LinkedIn" icon={Linkedin} value={form.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} testid="reg-linkedin" placeholder="https://linkedin.com/in/…" />
              <Input label="Página web" icon={Globe} value={form.website} onChange={(e) => set('website', e.target.value)} testid="reg-website" placeholder="https://tu-sitio.com" />
            </div>
          </Card>

          {/* 7. Idiomas */}
          <Card>
            <SectionTitle n="7" title="Idiomas" subtitle="Activos = interpretas hacia y desde. Pasivos = solo comprendes (de pasivo → activo)." />
            <div className="space-y-4">
              <TagSelect label="Idiomas activos" icon={Languages} value={form.active_languages} onChange={(v) => set('active_languages', v)} options={catalogs.languages} placeholder="Escribe y presiona Enter…" testid="reg-active-languages" hint="Agrega al menos uno. Si no está tu idioma, escríbelo y haz clic en “Agregar”." />
              <TagSelect label="Idiomas pasivos" icon={Languages} value={form.passive_languages} onChange={(v) => set('passive_languages', v)} options={catalogs.languages} placeholder="Escribe y presiona Enter…" testid="reg-passive-languages" hint="Si no está tu idioma, escríbelo y haz clic en “Agregar”." />
            </div>
          </Card>

          {/* 8. Modalidades */}
          <Card>
            <SectionTitle n="8" title="Modalidades de interpretación" subtitle="Simultánea, consecutiva, remota (RSI), etc." />
            <TagSelect label="Modalidades" icon={Mic} value={form.modalities} onChange={(v) => set('modalities', v)} options={catalogs.modalities} placeholder="Elige o escribe…" testid="reg-modalities" hint="Si no está tu modalidad, escríbela y haz clic en “Agregar”." />
          </Card>

          {/* 9. Especialidades */}
          <Card>
            <SectionTitle n="9" title="Especialidades" subtitle="Áreas temáticas en las que trabajas." />
            <TagSelect label="Especialidades" icon={Briefcase} value={form.specialty} onChange={(v) => set('specialty', v)} options={catalogs.specialties} placeholder="Ej. Médico, Jurídico, Finanzas…" testid="reg-specialty" hint="Si no está tu especialidad, escríbela y haz clic en “Agregar”." />
          </Card>

          {/* 10. Semblanza */}
          <Card>
            <SectionTitle n="10" title="Semblanza" subtitle="Cuéntale a los clientes sobre tu trayectoria." />
            <textarea
              value={form.bio}
              onChange={(e) => set('bio', e.target.value)}
              rows={5}
              data-testid="reg-bio"
              placeholder="Describe tu experiencia, formación y áreas de trabajo…"
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 resize-y"
            />
          </Card>

          <button type="submit" disabled={submitting} data-testid="register-confirm-submit-btn" className="w-full flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all disabled:opacity-50">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <><ShieldCheck size={16} />Enviar solicitud de registro</>}
          </button>
          <p className="text-[11px] text-charcoal-400 text-center leading-relaxed">Tu solicitud será revisada por el consejo directivo antes de publicarse en el directorio.</p>

          <div className="pt-2 text-center border-t border-gray-100">
            <p className="text-xs text-charcoal-400 mt-3 mb-2">¿Algo no funciona o tienes dudas?</p>
            <SupportContactButton
              source="register-confirm"
              defaultEmail={isGeneric ? form.email : (tokenInfo?.email || '')}
              getDiagnostics={() => ({ error_message: submitError, click_log: logRef.current })}
            />
          </div>
        </form>

        {/* Sticky preview */}
        <div className="lg:sticky lg:top-6">
          <button
            type="button"
            onClick={() => setShowFullPreview(true)}
            data-testid="open-full-preview-btn"
            className="w-full mb-3 inline-flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-colors shadow-sm"
          >
            <Eye size={16} /> Ver previsualización final
          </button>
          <InterpreterProfilePreview form={form} />
        </div>
      </div>

      {showFullPreview && <FullProfilePreview form={form} onClose={() => setShowFullPreview(false)} />}
    </Shell>
  );
}

function Card({ children }) {
  return <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6">{children}</div>;
}

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal-50 via-white to-cmic-50" data-testid="register-confirm-page">
      <div className="p-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-cmic-600 transition-colors">
          <ArrowLeft size={16} />Volver al inicio de sesión
        </Link>
      </div>
      <div className={`mx-auto px-4 pb-16 ${wide ? 'max-w-5xl' : 'max-w-md'}`}>
        <div className="flex flex-col items-center mb-8">
          <img src={logo} alt="CMIC" className="h-14 w-auto mb-3" />
          <h1 className="text-2xl font-bold text-charcoal-800">Completa tu registro</h1>
          <p className="text-sm text-charcoal-500 mt-2 text-center">Llena tu perfil de intérprete para el directorio del CMIC.</p>
        </div>
        {children}
      </div>
    </div>
  );
}
